import { useEffect, useState, useCallback } from 'react'
import { X, Search, UserPlus, Trash2, Loader2, UserMinus } from 'lucide-react'
import {
  useGetFaceQuery,
  useCreatePersonFromFacesMutation,
  useUpdatePersonMutation,
  useUnlinkPersonMutation,
  useSoftDeleteFacesMutation,
} from '@/store/api'
import { signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

interface FaceProfileModalProps {
  faceId: string
  onClose: () => void
  onFindSimilar: (faceId: string, cropUrl: string) => void
  onAssign: (faceId: string) => void
  onViewSource: (masterId: string) => void
}

export default function FaceProfileModal({
  faceId,
  onClose,
  onFindSimilar,
  onAssign,
  onViewSource,
}: FaceProfileModalProps) {
  useSignedUrls()
  const { data: face, isLoading, isError } = useGetFaceQuery(faceId)
  const [createPersonFromFaces] = useCreatePersonFromFacesMutation()
  const [updatePerson] = useUpdatePersonMutation()
  const [unlinkPerson] = useUnlinkPersonMutation()
  const [softDeleteFaces] = useSoftDeleteFacesMutation()

  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [isSavingName, setIsSavingName] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Initialize edit name from face data
  useEffect(() => {
    if (face?.person_name) {
      setEditName(face.person_name)
    }
  }, [face?.person_name])

  // Keyboard: Escape closes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmDelete) {
          setConfirmDelete(false)
        } else if (isEditing) {
          setIsEditing(false)
        } else {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, confirmDelete, isEditing])

  const handleSaveName = useCallback(async () => {
    if (!face || isSavingName) return
    const trimmed = editName.trim()
    if (!trimmed) return

    setIsSavingName(true)
    try {
      if (face.person_id) {
        await updatePerson({ personId: face.person_id, name: trimmed }).unwrap()
      } else {
        await createPersonFromFaces({ faceIds: [faceId], name: trimmed }).unwrap()
      }
      setIsEditing(false)
    } catch (error: unknown) {
      console.error('Failed to save name:', error)
    } finally {
      setIsSavingName(false)
    }
  }, [face, editName, faceId, isSavingName, updatePerson, createPersonFromFaces])

  const handleUnlink = useCallback(async () => {
    if (!face?.person_id) return
    try {
      await unlinkPerson({ faceIds: [faceId] }).unwrap()
    } catch (error: unknown) {
      console.error('Failed to unlink person:', error)
    }
  }, [face?.person_id, faceId, unlinkPerson])

  const handleDelete = useCallback(async () => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      await softDeleteFaces({ faceIds: [faceId] }).unwrap()
      onClose()
    } catch (error: unknown) {
      console.error('Failed to delete face:', error)
    } finally {
      setIsDeleting(false)
    }
  }, [faceId, isDeleting, softDeleteFaces, onClose])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">Face Profile</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-secondary transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {isLoading && (
            <div className="flex items-center justify-center h-48">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          )}

          {isError && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">Failed to load face details.</p>
            </div>
          )}

          {face && !isLoading && (
            <>
              {/* Face info section */}
              <div className="flex gap-5">
                {/* Large face crop */}
                <div className="w-36 h-36 rounded-xl overflow-hidden bg-secondary flex-shrink-0">
                  <img
                    src={signedSrc(face.crop_url)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Metadata */}
                <div className="flex-1 min-w-0 space-y-2">
                  {/* Name (editable) */}
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveName()
                          if (e.key === 'Escape') setIsEditing(false)
                        }}
                        className="w-full px-2 py-1 rounded bg-secondary border border-cyan-500 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-cyan-500"
                        placeholder="Enter name..."
                        autoFocus
                      />
                      <button
                        onClick={handleSaveName}
                        disabled={isSavingName}
                        className="px-2 py-1 text-xs font-medium rounded bg-cyan-600 hover:bg-cyan-700 text-white transition-colors disabled:opacity-50 flex-shrink-0"
                      >
                        {isSavingName ? '...' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditName(face.person_name || '')
                        setIsEditing(true)
                      }}
                      className="text-left w-full"
                    >
                      <span className={`text-base font-medium ${face.person_name ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                        {face.person_name || 'Unnamed — click to name'}
                      </span>
                    </button>
                  )}

                  {/* Person assignment */}
                  {face.person_id && face.person_name && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-cyan-400 bg-cyan-400/10 px-2 py-0.5 rounded">
                        Person: {face.person_name}
                      </span>
                      <button
                        onClick={handleUnlink}
                        className="p-0.5 text-muted-foreground hover:text-red-400 transition-colors"
                        title="Unlink from person"
                      >
                        <UserMinus size={12} />
                      </button>
                    </div>
                  )}

                  {/* Confidence */}
                  <div className="text-xs text-muted-foreground">
                    Confidence: {Math.round(face.detection_confidence * 100)}%
                  </div>

                  {/* Age / Gender */}
                  {(face.age != null || face.gender) && (
                    <div className="text-xs text-muted-foreground">
                      {[
                        face.age != null ? `Age: ~${face.age}` : null,
                        face.gender ?? null,
                      ]
                        .filter(Boolean)
                        .join(' | ')}
                    </div>
                  )}

                  {/* Primary badge */}
                  {face.is_primary && (
                    <span className="inline-block text-xs bg-cyan-600/20 text-cyan-400 px-2 py-0.5 rounded">
                      Primary face
                    </span>
                  )}
                </div>
              </div>

              {/* Source image section */}
              {face.source_thumbnail_url && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="text-xs text-muted-foreground mb-2">Source Image</div>
                  <button
                    onClick={() => onViewSource(face.master_id)}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors w-full text-left"
                  >
                    <div className="w-16 h-12 rounded overflow-hidden bg-secondary flex-shrink-0">
                      <img
                        src={signedSrc(face.source_thumbnail_url)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-foreground truncate">
                        {face.source_file_name || face.master_id}
                      </div>
                      <div className="text-xs text-muted-foreground">Click to view</div>
                    </div>
                  </button>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                <button
                  onClick={() => onFindSimilar(faceId, signedSrc(face.crop_url))}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Search size={14} />
                  Find Similar
                </button>

                <button
                  onClick={() => onAssign(faceId)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg text-sm transition-colors"
                >
                  <UserPlus size={14} />
                  Assign to Person
                </button>

                {/* Delete with confirmation */}
                {confirmDelete ? (
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-xs text-muted-foreground">Delete face?</span>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="px-2 py-1 text-xs rounded bg-secondary hover:bg-secondary/80 text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      Delete
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/10 hover:bg-red-600/20 text-red-400 rounded-lg text-sm transition-colors ml-auto"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
